// components/protocolos/formSections/ChatGroupsManager.jsx
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faCircleMinus, faEdit, faSave, faTimes } from '@fortawesome/free-solid-svg-icons'; // Importar iconos de FontAwesome
import { toast } from 'react-toastify';
import Swal from 'sweetalert2'; // Importar SweetAlert2

export const ChatGroupsManager = forwardRef (({ initialGroups = [], cedisId, onPostChatGroup, onPutChatGroup, onDeleteChatGroup }, ref) => {
    // Estado interno para la lista de grupos de mensajería
    const [gruposMensajeria, setGruposMensajeria] = useState([]);
    // Estado para el formulario de añadir nuevo grupo
    const [newGroup, setNewGroup] = useState({
        nombre_grupo: '',
        url: '',
        tipo_id: '' // Añadido tipo_id para el nuevo grupo
    });
    // Estado para el ID del grupo que se está editando
    const [editingId, setEditingId] = useState(null);
    // Estado para los datos del formulario de edición
    const [editFormData, setEditFormData] = useState({
        nombre_grupo: '',
        url: '',
        // tipo_id: '' // Añadido tipo_id para la edición
    });

    // Referencias para las funciones de API pasadas por props
    const onPostChatGroupRef = useRef(onPostChatGroup);
    const onPutChatGroupRef = useRef(onPutChatGroup);
    const onDeleteChatGroupRef = useRef(onDeleteChatGroup);

    // Referencia para el estado actual de 'gruposMensajeria' para usar en callbacks
    const gruposMensajeriaRef = useRef(gruposMensajeria);

    // Actualizar las referencias si las funciones o el estado cambian
    useEffect(() => {
        onPostChatGroupRef.current = onPostChatGroup;
        onPutChatGroupRef.current = onPutChatGroup;
        onDeleteChatGroupRef.current = onDeleteChatGroup;
    }, [onPostChatGroup, onPutChatGroup, onDeleteChatGroup]);

    // Sincronizar gruposMensajeriaRef.current con el estado actual de 'gruposMensajeria'
    useEffect(() => {
        gruposMensajeriaRef.current = gruposMensajeria;
    }, [gruposMensajeria]);

    // Sincronizar el estado interno con las props iniciales cuando cambian.
    useEffect(() => {
        // console.log("[ChatGroupsManager - useEffect] initialGroups recibido al sincronizar:", initialGroups);
        // Asegurarse de que initialGroups sea un array antes de mapear
        if (initialGroups && Array.isArray(initialGroups)) { // Eliminado .length > 0 aquí
            const mappedInitialGroups = initialGroups.map(group => ({
                // Usamos grupo_mensajeria_id si existe, si no un TEMP_ID
                id: group.grupo_mensajeria_id || `TEMP_${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                grupo_mensajeria_id: group.grupo_mensajeria_id, // Guardamos el ID del backend
                nombre_grupo: group.nombre_grupo || '',
                url: group.url || '',
                tipo_id: group.tipo_id || ''
            }));
            setGruposMensajeria(mappedInitialGroups);
        } else {
            setGruposMensajeria([]); // Si no hay datos o no es un array, inicializar vacío
        }
    }, [initialGroups]);

    // Exponer métodos imperativos para que el componente padre (o un hook) pueda interactuar.
    useImperativeHandle(ref, () => ({
        // Método para obtener los datos de los grupos de chat, incluyendo IDs temporales
        getChatGroupsData: () => {
            return gruposMensajeria.map(group => ({
                id: group.id, // Incluimos el ID local (TEMP_ o real)
                grupo_mensajeria_id: String(group.id).startsWith('TEMP_') ? null : group.grupo_mensajeria_id,
                nombre_grupo: group.nombre_grupo,
                url: group.url,
                tipo_id: Number(group.tipo_id),
            }));
        },
        resetGroups: () => {
            // console.log("[ChatGroupsManager] Reseteando grupos y formulario.");
            setGruposMensajeria([]);
            setNewGroup({ nombre_grupo: '', url: '', tipo_id: '' });
            setEditingId(null);
            setEditFormData({ nombre_grupo: '', url: '', tipo_id: '' });
        }
    }));

    // Helper para obtener el nombre del tipo de ID
    const getTipoIdName = (id) => {
        switch (Number(id)) {
            case 1:
                return 'Whatsapp';
            case 2:
                return 'Telegram';
            default:
                return 'Desconocido';
        }
    };

    // Maneja los cambios en los inputs de añadir
    const handleNewChange = (e) => {
        const { name, value } = e.target;
        setNewGroup(prev => ({ ...prev, [name]: value }));
    };

    // Maneja los cambios en los inputs de edición
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    // Añadir un nuevo grupo de chat
    const handleAddChatGroup = useCallback(async () => {
        const trimmedNombre = newGroup.nombre_grupo.trim();
        const trimmedUrl = newGroup.url.trim();

        if (!trimmedNombre || !trimmedUrl || !newGroup.tipo_id) {
            toast.error("Nombre del grupo, URL y Tipo de ID son obligatorios.");
            return;
        }
        if (isNaN(Number(newGroup.tipo_id))) {
            toast.error("El Tipo de ID debe ser un número válido.");
            return;
        }
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            toast.error("La URL debe comenzar con 'http://' o 'https://'.");
            return;
        }

        // Prevenir duplicados (por la URL o el nombre)
        const currentGroupsInTable = gruposMensajeriaRef.current;
        const isDuplicate = currentGroupsInTable.some(group =>
            group.url === trimmedUrl || group.nombre_grupo === trimmedNombre
        );

        if (isDuplicate) {
            toast.warn("Este grupo de chat (por URL o nombre) ya ha sido añadido.");
            return;
        }

        // 1. Adición visual (Pseudo-POST)
        const tempId = `TEMP_${Date.now()}`;
        setGruposMensajeria(prevGroups => {
            const updatedGroups = [
                ...prevGroups,
                {
                    id: tempId,
                    grupo_mensajeria_id: null, // ID de backend nulo para nuevos
                    nombre_grupo: trimmedNombre,
                    url: trimmedUrl,
                    tipo_id: Number(newGroup.tipo_id),
                }
            ];
            // console.log("[ChatGroupsManager] Nuevo estado de 'gruposMensajeria' DESPUÉS de pseudo-POST:", updatedGroups);
            return updatedGroups;
        });
        // toast.info('Grupo de chat añadido visualmente. Procesando guardado en servidor...');

        // toast.info('Grupo de chat añadido localmente. Se guardará al guardar el protocolo principal.');
        setNewGroup({ nombre_grupo: '', url: '', tipo_id: '' });
        // console.log("[ChatGroupsManager] Formulario de nuevo grupo reseteado.");
    }, [newGroup, gruposMensajeriaRef]); // Eliminadas dependencias relacionadas con API y cedisId

    // Iniciar edición de un grupo
    const handleEditClick = useCallback((group) => {
        setEditingId(group.id);
        setEditFormData({
            nombre_grupo: group.nombre_grupo,
            url: group.url,
            // tipo_id: group.tipo_id
        });
    }, []);

    // Guardar los cambios de un grupo editado
    const handleSaveEdit = useCallback(async (groupId) => {
        const trimmedNombre = editFormData.nombre_grupo.trim();
        const trimmedUrl = editFormData.url.trim();

        if (!trimmedNombre || !trimmedUrl) {
            toast.error("Nombre del grupo, URL y Tipo de ID no pueden estar vacíos.");
            return;
        }
        // if (isNaN(Number(editFormData.tipo_id))) {
        //     toast.error("El Tipo de ID debe ser un número válido.");
        //     return;
        // }
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
            toast.error("La URL debe comenzar con 'http://' o 'https://'.");
            return;
        }

        // Prevenir duplicados (excluyendo el grupo que se está editando)
        const currentGroupsInTable = gruposMensajeriaRef.current;
        const isDuplicate = currentGroupsInTable.some(group =>
            (group.id !== groupId && (group.url === trimmedUrl || group.nombre_grupo === trimmedNombre))
        );

        if (isDuplicate) {
            toast.warn("Ya existe otro grupo de chat con la misma URL o nombre.");
            return;
        }

        const groupToUpdate = gruposMensajeria.find(group => group.id === groupId);
        if (!groupToUpdate || !groupToUpdate.grupo_mensajeria_id) {
            toast.error("No se puede editar un grupo no guardado en el servidor.");
            return;
        }

        const payload = {
            nombre_grupo: trimmedNombre,
            url: trimmedUrl,
            // tipo_id: Number(editFormData.tipo_id)
        };
        // console.log("[ChatGroupsManager] Enviando PUT de grupo de chat:", payload);

        try {
            const result = await onPutChatGroupRef.current(groupToUpdate.grupo_mensajeria_id, payload);
            // console.log("[ChatGroupsManager] Resultado del PUT de grupo de chat:", result);

            if (result.ok) {
                // toast.success(result.msg || "Grupo de chat actualizado con éxito!");
                setEditingId(null); // Salir del modo de edición
                // El padre (ModalProtocolos) recargará los grupos después del éxito.
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al actualizar el grupo',
                    text: result.msg || "Ocurrió un error al actualizar el grupo de chat en el servidor.",
                    confirmButtonText: 'Entendido'
                });
            }
        } catch (error) {
            console.error("Error al actualizar grupo de chat (catch):", error);
            Swal.fire({
                icon: 'error',
                title: 'Error inesperado',
                text: "Ocurrió un error inesperado al actualizar el grupo de chat. Por favor, intenta de nuevo.",
                confirmButtonText: 'Entendido'
            });
        }
    }, [editFormData, onPutChatGroupRef, gruposMensajeria, gruposMensajeriaRef]);

    // Cancelar la edición
    const handleCancelEdit = useCallback(() => {
        setEditingId(null);
    }, []);

    // Eliminar un grupo de chat
    const handleRemoveChatGroup = useCallback(async (groupToRemoveId) => {
        const groupToDelete = gruposMensajeriaRef.current.find(group => group.id === groupToRemoveId);

        if (!groupToDelete) {
            toast.error("Grupo no encontrado.");
            return;
        }

        // Confirmación con SweetAlert2
        const resultSwal = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a eliminar el grupo de chat "${groupToDelete.nombre_grupo}". ¡Esta acción no se puede deshacer!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!resultSwal.isConfirmed) {
            return; // El usuario canceló la eliminación
        }

        // Solo intentar eliminar del backend si el grupo tiene un ID de backend
        if (groupToDelete.grupo_mensajeria_id) {
            // console.log("[ChatGroupsManager] Enviando DELETE de grupo de chat con ID:", groupToDelete.grupo_mensajeria_id);

            try {
                const result = await onDeleteChatGroupRef.current(groupToDelete.grupo_mensajeria_id);
                // console.log("[ChatGroupsManager] Resultado del DELETE de grupo de chat:", result);

                if (result.ok) {
                    // toast.success(result.msg || "Grupo de chat eliminado con éxito!");
                    // El padre (ModalProtocolos) recargará los grupos después del éxito.
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al eliminar',
                        text: result.msg || "Ocurrió un error al eliminar el grupo de chat del servidor.",
                        confirmButtonText: 'Entendido'
                    });
                }
            } catch (error) {
                console.error("Error al eliminar grupo de chat:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error inesperado',
                    text: "Ocurrió un error inesperado al eliminar el grupo de chat.",
                    confirmButtonText: 'Entendido'
                });
            }
        } else {
            // Si es un grupo nuevo (con ID temporal), solo eliminar visualmente
            toast.warn("Grupo de chat eliminado.");
            setGruposMensajeria(prevGroups => prevGroups.filter(group => group.id !== groupToRemoveId));
        }
    }, [onDeleteChatGroupRef, gruposMensajeriaRef]);

    // El botón de agregar grupo se deshabilita si no hay un cedisId válido
    const isAddButtonDisabled = !cedisId;

    // Log para ver el estado de gruposMensajeria justo antes de renderizar
    // console.log("[ChatGroupsManager - Render] gruposMensajeria state:", gruposMensajeria);


    return (
        <Form.Group className="mb-3">
            <Form.Label>Administrar Grupos de Mensajería</Form.Label>
            <Row className="mb-2">
                <Col sm="4">
                    <Form.Control
                        type="text"
                        name="nombre_grupo"
                        placeholder="Nombre del Grupo"
                        value={newGroup.nombre_grupo}
                        onChange={handleNewChange}
                    />
                </Col>
                <Col sm="4">
                    <Form.Control
                        type="url"
                        name="url"
                        placeholder="URL del Grupo (https://...)"
                        value={newGroup.url}
                        onChange={handleNewChange}
                        style={{ height: "3.25rem"}}
                    />
                </Col>
                <Col sm="3" className='align-content-center'>
                    <Form.Select
                        name="tipo_id"
                        value={newGroup.tipo_id}
                        onChange={handleNewChange}
                        style={{ 
                            height: "3.25rem", 
                            border: "0px",
                            backgroundColor: "#ffefe",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.15)"
                            
                        }}
                    >
                        <option value="">Seleccione Tipo</option>
                        <option value="1">Whatsapp</option>
                        <option value="2">Telegram</option>
                    </Form.Select>
                </Col>
                <Col sm="1" className='d-flex align-items-center' style={{height: "4rem"}}>
                    <FontAwesomeIcon
                        icon={faCirclePlus}
                        className={`text-primary fa-2x ${isAddButtonDisabled ? 'text-muted' : ''}`}
                        style={{ cursor: isAddButtonDisabled ? 'not-allowed' : 'pointer' }}
                        onClick={isAddButtonDisabled ? null : handleAddChatGroup}
                        title={isAddButtonDisabled ? "Seleccione un CEDIS para agregar grupos" : "Agregar Grupo de Chat"}
                    />
                </Col>
            </Row>
            <div>
                {gruposMensajeria.length > 0 ? (
                    <ul className="list-group">
                        {gruposMensajeria.map(group => (
                            <li key={group.id} className="list-group-item d-flex justify-content-between align-items-center">
                                {editingId === group.id ? (
                                    <Row className="flex-grow-1 align-items-center">
                                        <Col md={4}>
                                            <Form.Control
                                                type="text"
                                                name="nombre_grupo"
                                                value={editFormData.nombre_grupo}
                                                onChange={handleEditChange}
                                                placeholder="Nombre del Grupo"
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Control
                                                type="url"
                                                name="url"
                                                value={editFormData.url}
                                                onChange={handleEditChange}
                                                placeholder="URL del Grupo"
                                            />
                                        </Col>
                                        {/* <Col md={2}>
                                            <Form.Select
                                                name="tipo_id"
                                                value={editFormData.tipo_id}
                                                onChange={handleEditChange}
                                            >
                                                <option value="">Seleccione Tipo</option>
                                                <option value="1">Whatsapp</option>
                                                <option value="2">Telegram</option>
                                            </Form.Select>
                                        </Col */}
                                        <Col md={2} className="text-end">
                                            <FontAwesomeIcon
                                                icon={faSave}
                                                className="text-success fa-2x me-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSaveEdit(group.id)}
                                                title="Guardar Cambios"
                                            />
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                className="text-secondary fa-2x"
                                                style={{ cursor: 'pointer' }}
                                                onClick={handleCancelEdit}
                                                title="Cancelar Edición"
                                            />
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row className="flex-grow-1 align-items-center">
                                        <Col md={4}><strong>{group.nombre_grupo}</strong></Col>
                                        <Col md={4}>
                                            <a
                                                href={group.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ 
                                                    color: '#007bff',
                                                    display: 'block', /* Hace que el 'a' ocupe todo el ancho disponible */
                                                    whiteSpace: 'nowrap', /* Evita que el texto se rompa en varias líneas */
                                                    overflow: 'hidden', /* Oculta el texto que se desborda */
                                                    textOverflow: 'ellipsis' /* Muestra puntos suspensivos (...) para el texto oculto */
                                                }}
                                            >
                                                {group.url}
                                            </a>
                                        </Col>
                                        <Col md={2}>Tipo ID: {getTipoIdName(group.tipo_id)}</Col>
                                        <Col md={2} className="text-end">
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="text-info fa-2x me-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleEditClick(group)}
                                                title="Editar Grupo de Chat"
                                            />
                                            <FontAwesomeIcon
                                                icon={faCircleMinus}
                                                className="text-danger fa-2x"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRemoveChatGroup(group.id)}
                                                title="Eliminar Grupo de Chat"
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-muted">No hay grupos de chat configurados.</p>
                )}
            </div>
        </Form.Group>
    );
});
